/**
 *
 * DynamicSelect
 *
 */

import React, { memo, useEffect, useState } from "react";
import classNames from "classnames";
import ReactDOM from "react-dom";

// styles
// antd component
import { Checkbox, Col, Form, Select, Skeleton } from "antd";

import useAsync from "./useAsync";
import ColFormItemWrapper from "../../components/CustomForm/ColWFormItemWrapper";
import SelectCustom from "../../components/CustomForm/SelectCustom";
import { http } from "../../../engine/api/http";
import { ImConfused } from "react-icons/im";
import { BiCheckCircle } from "react-icons/bi";
import { isFunction } from "webpack-merge/dist/utils";

const { Option } = Select;
import css from "./dynamic.css";
import { normalizeString } from "../../components/CustomForm/form.helpers";
// icons
//import CustomIcons from 'components/CustomIcons';

function DynamicSelect({
  endPoint,
  itemCallback,
  index,
  itemsCallBack,
  filterItem = () => {
    return true;
  },
  item,
  onSelect,
  refForm,
  displayLabel,
  disabled,
  className,
  updateEndPoint,
  ready = true,
}) {
  const childClassNames = classNames(
    `select-axios-wrapper ${
      item.inputSize ? ` ${item.inputSize}-input-select ` : ""
    }`, // small, middle, large
    className,
  );

  const { isLoading, isSuccess, run, data } = useAsync();
  const [randomKey, setRandomKey] = useState(null);

  const [listOptions, setListOptions] = useState([]);
  const [readyToRender, setReadyToRender] = useState(false);

  const watchedDynamicValue = item.watchDynamicObject
    ? Form.useWatch(item.watchDynamicObject)
    : null;

  const httpCall = () => {
    try {
      const { method = "GET" } = item;

      let url = "";

      if (isFunction(endPoint)) {
        url = endPoint(watchedDynamicValue);
      } else {
        url = endPoint;
      }

      if (method === "GET") {
        run(
          http.get(url).then(res => {
            return res.data.filter(filterItem);
          }),
        );
      } else if (method === "POST") {
        run(
          http.post(url, item.body).then(res => {
            return res.data.filter(filterItem);
          }),
        );
      }
    } catch (e) {
      console.log(e);
    }
  };
  const doTheRun = () => {
    if (item.watchDynamicObject) {
      if (watchedDynamicValue !== undefined) {
        httpCall();
      }
    } else {
      httpCall();
    }
  };

  useEffect(() => {
    if (watchedDynamicValue !== undefined) {
      httpCall();
    }
  }, [watchedDynamicValue]);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    if (endPoint && ready && listOptions.length === 0) {
      if (item.watchDynamicObject) {
        if (watchedDynamicValue !== undefined) {
          doTheRun();
        }
      } else {
        doTheRun();
      }
    }
  }, [endPoint, ready, watchedDynamicValue]);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    if (data) {
      if (refForm !== undefined) {
        refForm.current[`_${item.name}`] = data;
      }

      itemsCallBack(data);
      const optioins = data.map(elem => itemCallback(elem));
      setListOptions(optioins);

      setReadyToRender(true);
    }
  }, [data]);

  // This hook is used to update the list on component when a variable is triggered (like from parent)
  useEffect(() => {
    if (updateEndPoint.length > 0 && ready) {
      doTheRun();
      setRandomKey(Math.random());
    }
  }, updateEndPoint);

  let extra = {};

  if (isLoading) {
    return (
      <Col style={{}} key={Math.random() + `-col-${index}`} span={12}>
        <Form.Item label={item.label}>
          <Skeleton.Input style={{ width: "100%" }} />
        </Form.Item>
      </Col>
    );
  }

  const extractTextFromJSX = children => {
    if (typeof children === "string") {
      return children;
    }

    if (Array.isArray(children)) {
      return children.map(extractTextFromJSX).join("");
    }

    if (children && children.props && children.props.children) {
      return extractTextFromJSX(children.props.children);
    }

    return "";
  };

  const filterOption = (input, option) => {
    let textContent = "";

    if (
      option.children &&
      option.children.$$typeof === Symbol.for("react.element")
    ) {
      textContent = extractTextFromJSX(option.children);
    } else {
      textContent = option.children;
    }

    return normalizeString(textContent).includes(normalizeString(input));
  };

  return (
    <>
      {isSuccess || ready === false ? (
        <ColFormItemWrapper
          item={item}
          key={randomKey + index}
          refForm={refForm}
        >
          {item.multiple ? (
            <SelectCustom
              key={randomKey + index + "SelectCustom"}
              className={childClassNames}
              ready={item.ready}
              item={item}
              refForm={refForm}
              renderExtraSelect={item.multiple}
              listOptions={listOptions}
              autoFocus={true}
              dropdownMatchSelectWidth={false}
              placeholder={
                item.placeholder !== undefined ? item.placeholder : item.label
              }
              allowClear={!!item.allowClear}
              disabled={disabled}
              optionFilterProp="children"
              showSearch
              menuItemSelectedIcon={<BiCheckCircle />}
              mode={item.multiple ? "multiple" : "tag"}
              filterOption={filterOption}
              filterSort={(optionA, optionB) =>
                normalizeString(optionA.children).localeCompare(
                  normalizeString(optionB.children),
                )
              }
              {...extra}
            />
          ) : (
            <Select
              disabled={disabled}
              className={childClassNames}
              dropdownMatchSelectWidth={false}
              placeholder={
                item.placeholder !== undefined ? item.placeholder : item.label
              }
              allowClear={item.allowClear ? true : false}
              optionFilterProp="children"
              onSelect={onSelect}
              showSearch
              // open={true}
              mode={item.multiple ? "multiple" : "tag"}
              filterOption={filterOption}
              menuItemSelectedIcon={<BiCheckCircle />}
              dropdownRender={ReactNode => {
                return ReactNode;
              }}
              filterSort={
                item?.noSorting
                  ? null
                  : (optionA, optionB) => {
                      return normalizeString(optionA.children).localeCompare(
                        normalizeString(optionB.children),
                      );
                    }
              }
              {...extra}
            >
              {listOptions.map((option, i) => {
                return (
                  <Option
                    {...option}
                    key={option?.key || option?.value}
                    value={option?.value || option?.key}
                    color={option?.color}
                  >
                    {option?.label || option?.value}
                  </Option>
                );
              })}
            </Select>
          )}
        </ColFormItemWrapper>
      ) : null}
    </>
  );
}

DynamicSelect.propTypes = {};

DynamicSelect.defaultProps = {
  item: { itemsCallBack: () => {} },
  updateEndPoint: [],
};

export default memo(DynamicSelect);
